import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  MapPin, Calendar, Users, Star, Check, X as XIcon, ChevronRight, Phone
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import { toast } from 'sonner';

export default function PackageDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [packageData, setPackageData] = useState(null);
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchPackage();
  }, [id]);

  const fetchPackage = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/packages`);
      const pkg = response.data.find(p => (p._id || p.id) === id);
      if (pkg) {
        setPackageData(pkg);
        // Auto-expand first variant if exists
        if (pkg.duration_variants && pkg.duration_variants.length > 0) {
          setSelectedVariantIndex(0);
        }
      } else {
        toast.error('Package not found');
        navigate('/nammayatra');
      }
    } catch (error) {
      console.error('Failed to fetch package:', error);
      toast.error('Failed to load package');
    } finally {
      setLoading(false);
    }
  };

  const handleBookNow = () => {
    if (!packageData || !selectedVariant) {
      toast.error('Please select a duration');
      return;
    }
    navigate('/package-booking', {
      state: {
        package: packageData,
        selectedVariant: selectedVariant,
        variantIndex: selectedVariantIndex
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!packageData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Package Not Found</h2>
          <Link to="/nammayatra" className="text-green-600 hover:underline">Back to Packages</Link>
        </div>
      </div>
    );
  }

  const selectedVariant = packageData.duration_variants?.[selectedVariantIndex];
  const hasVariants = packageData.duration_variants && packageData.duration_variants.length > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative h-96 bg-gradient-to-r from-green-800 to-green-900">
        <img
          src={packageData.image_url || 'https://images.unsplash.com/photo-1609137144813-7d9921338f24?w=1200'}
          alt={packageData.name}
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        
        <div className="absolute bottom-0 left-0 right-0 px-4 sm:px-6 lg:px-8 pb-8">
          <div className="max-w-7xl mx-auto">
            <nav className="mb-4 text-sm">
              <Link to="/" className="text-yellow-200 hover:text-yellow-100">Home</Link>
              <span className="mx-2 text-yellow-200">/</span>
              <Link to="/nammayatra" className="text-yellow-200 hover:text-yellow-100">NammaYatra</Link>
              <span className="mx-2 text-yellow-200">/</span>
              <span className="text-white">{packageData.name}</span>
            </nav>
            
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-3">
                  <Badge className="bg-yellow-400 text-green-900">{packageData.category}</Badge>
                  {packageData.featured && (
                    <Badge className="bg-green-600 text-white">Featured</Badge>
                  )}
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">{packageData.name}</h1>
                <div className="flex items-center space-x-6 text-yellow-100">
                  <div className="flex items-center space-x-2">
                    <MapPin className="w-5 h-5" />
                    <span>{packageData.destination}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Star className="w-5 h-5 text-yellow-400 fill-current" />
                    <span className="font-semibold">{packageData.rating || 5}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">About This Package</h2>
                <p className="text-gray-700 leading-relaxed">{packageData.description}</p>
              </CardContent>
            </Card>

            {/* Duration Selection */}
            {hasVariants && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Select Duration</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                    {packageData.duration_variants.map((variant, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedVariantIndex(index)}
                        className={`p-4 border-2 rounded-lg text-left transition-all ${
                          selectedVariantIndex === index
                            ? 'border-green-600 bg-green-50'
                            : 'border-gray-200 hover:border-green-300'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <Calendar className="w-5 h-5 text-green-600" />
                            <span className="font-semibold text-gray-900">{variant.duration_text}</span>
                          </div>
                          {selectedVariantIndex === index && (
                            <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                        <div className="text-sm text-gray-600 mt-2">
                          <Users className="w-4 h-4 inline mr-1" />
                          Up to {variant.max_people} travelers
                        </div>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Itinerary */}
            {selectedVariant && selectedVariant.itinerary && selectedVariant.itinerary.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Day-by-Day Itinerary</h2>
                  <div className="space-y-6">
                    {selectedVariant.itinerary.map((day, index) => (
                      <div key={index} className="flex">
                        <div className="flex-shrink-0 w-16 h-16 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
                          {day.day}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">{day.title}</h3>
                          <p className="text-gray-700 leading-relaxed whitespace-pre-line">{day.activities}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Inclusions & Exclusions */}
            {selectedVariant && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Inclusions */}
                {selectedVariant.inclusions && selectedVariant.inclusions.length > 0 && (
                  <Card>
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-4">What's Included</h3>
                      <ul className="space-y-2">
                        {selectedVariant.inclusions.map((item, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Exclusions */}
                {selectedVariant.exclusions && selectedVariant.exclusions.length > 0 && (
                  <Card>
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-4">What's Not Included</h3>
                      <ul className="space-y-2">
                        {selectedVariant.exclusions.map((item, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <XIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>

          {/* Sidebar - Booking Card */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <Card className="border-2 border-green-200">
                <CardContent className="p-6 space-y-6">
                  {selectedVariant ? (
                    <>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Price per person</p>
                        <div className="text-4xl font-bold text-green-700">₹{selectedVariant.price.toLocaleString()}</div>
                        <div className="flex items-center space-x-2 mt-2 text-sm text-gray-600">
                          <Calendar className="w-4 h-4" />
                          <span>{selectedVariant.duration_text}</span>
                        </div>
                      </div>

                      <Button
                        onClick={handleBookNow}
                        className="w-full py-6 text-lg bg-green-600 hover:bg-green-700"
                      >
                        Book Now
                        <ChevronRight className="w-5 h-5 ml-2" />
                      </Button>

                      <div className="border-t pt-4 space-y-3 text-sm text-gray-600">
                        {packageData.benefits && packageData.benefits.length > 0 ? (
                          packageData.benefits.map((benefit, index) => (
                            <div key={index} className="flex items-center space-x-2">
                              <Check className="w-4 h-4 text-green-600" />
                              <span>{benefit}</span>
                            </div>
                          ))
                        ) : (
                          <>
                            <div className="flex items-center space-x-2">
                              <Check className="w-4 h-4 text-green-600" />
                              <span>Best price guarantee</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Check className="w-4 h-4 text-green-600" />
                              <span>Free cancellation up to 48 hours</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Check className="w-4 h-4 text-green-600" />
                              <span>24/7 customer support</span>
                            </div>
                          </>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No duration variants available
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Contact Card */}
              <Card className="mt-6">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-3">
                    {packageData.help_title || 'Need Help?'}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    {packageData.help_description || 'Our travel experts are here to assist you'}
                  </p>
                  <a
                    href={`tel:${packageData.help_phone || '+918001234567'}`}
                    className="flex items-center justify-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg font-semibold transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    <span>Call Now</span>
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
